import { MapPin, Navigation, Clock, AlertTriangle } from 'lucide-react';
import type { StoreCategory } from './StoreScoreBadge';

interface RouteSummaryBarProps {
  storeCount: number;
  totalDistanceKm?: number | null;
  estimatedDurationMin?: number | null;
  urgentCount: number;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}min`;
  return `${h}h${m > 0 ? String(m).padStart(2, '0') : ''}`;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(0)}km`;
}

/** Counts how many stores have "urgent" categories */
export function countUrgentStores(
  stores: Array<{ category?: StoreCategory | string | null }>
): number {
  const urgentCategories = new Set(['RUPTURA_CRITICA', 'SCORE_BAIXO', 'SEM_VISITA_LONGA']);
  return stores.filter((s) => s.category && urgentCategories.has(s.category)).length;
}

export default function RouteSummaryBar({
  storeCount,
  totalDistanceKm,
  estimatedDurationMin,
  urgentCount,
}: RouteSummaryBarProps) {
  return (
    <div className="flex items-center gap-3 overflow-x-auto text-xs font-medium text-gray-600 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <MapPin className="h-3.5 w-3.5 text-primary" />
        <span>{storeCount} lojas</span>
      </div>

      {totalDistanceKm != null && totalDistanceKm > 0 && (
        <>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Navigation className="h-3.5 w-3.5 text-blue-500" />
            <span>{formatDistance(totalDistanceKm)}</span>
          </div>
        </>
      )}

      {estimatedDurationMin != null && estimatedDurationMin > 0 && (
        <>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-1 whitespace-nowrap">
            <Clock className="h-3.5 w-3.5 text-purple-500" />
            <span>~{formatDuration(estimatedDurationMin)}</span>
          </div>
        </>
      )}

      {urgentCount > 0 && (
        <>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-1 whitespace-nowrap text-red-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{urgentCount} urgente{urgentCount > 1 ? 's' : ''}</span>
          </div>
        </>
      )}
    </div>
  );
}
